import { useState, useEffect, useCallback, useRef } from 'react';

// Global audio unlock state - persists across hook instances
const AUDIO_UNLOCKED_KEY = 'clothybazar_audio_unlocked';
let audioUnlocked = false;
let globalAudioCtx: AudioContext | null = null;

function getGlobalAudioCtx(): AudioContext | null {
  try {
    if (!globalAudioCtx || globalAudioCtx.state === 'closed') {
      globalAudioCtx = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }
    return globalAudioCtx;
  } catch {
    return null;
  }
}

// Unlock audio context - must be called from a user gesture
export async function unlockAudio(): Promise<boolean> {
  try {
    const ctx = getGlobalAudioCtx();
    if (!ctx) return false;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    // Play a silent buffer to fully unlock
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
    audioUnlocked = ctx.state === 'running';
    // Persist unlock so banner doesn't show again after page reload
    if (audioUnlocked) {
      try { localStorage.setItem(AUDIO_UNLOCKED_KEY, '1'); } catch { /* ignore */ }
    }
    return audioUnlocked;
  } catch {
    return false;
  }
}

// Check if user has previously unlocked audio (survived page reload)
export function hasUserEverUnlockedAudio(): boolean {
  try {
    return localStorage.getItem(AUDIO_UNLOCKED_KEY) === '1';
  } catch {
    return false;
  }
}

function playRingTone(ctx: AudioContext): void {
  const playBeep = (freq: number, start: number, duration: number, gain: number) => {
    try {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gainNode.gain.setValueAtTime(0, ctx.currentTime + start);
      gainNode.gain.linearRampToValueAtTime(gain, ctx.currentTime + start + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + start + duration - 0.05);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    } catch { /* ignore */ }
  };
  playBeep(880, 0, 0.3, 0.8);
  playBeep(660, 0.35, 0.3, 0.8);
  playBeep(880, 0.7, 0.3, 0.8);
  playBeep(660, 1.05, 0.5, 0.8);
}

async function tryPlayRingTone(): Promise<void> {
  try {
    const ctx = getGlobalAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    if (ctx.state === 'running') {
      playRingTone(ctx);
    }
  } catch { /* ignore */ }
}

export function useAudioUnlockStatus() {
  // Initialize from localStorage so banner stays hidden after page reload
  const [unlocked, setUnlocked] = useState(() => {
    if (hasUserEverUnlockedAudio()) {
      audioUnlocked = true;
      return true;
    }
    return audioUnlocked;
  });
  
  useEffect(() => {
    const check = setInterval(() => {
      if (globalAudioCtx?.state === 'running') {
        audioUnlocked = true;
        setUnlocked(true);
      }
    }, 500);
    return () => clearInterval(check);
  }, []);
  
  return unlocked;
}

export function useOrderNotification() {
  const stateRef = useRef({
    isRinging: false,
    intervalId: null as ReturnType<typeof setInterval> | null,
  });

  const doRing = useCallback(() => {
    const s = stateRef.current;
    if (!s.isRinging) return;

    // Vibrate - works on Android even without audio unlock
    try {
      if (navigator.vibrate) navigator.vibrate([600, 200, 600, 200, 600]);
    } catch { /* ignore */ }

    // Sound - try to play
    tryPlayRingTone().catch(() => { /* ignore */ });
  }, []);

  const startRinging = useCallback(() => {
    const s = stateRef.current;
    if (s.isRinging) return;

    if (s.intervalId !== null) {
      clearInterval(s.intervalId);
      s.intervalId = null;
    }

    s.isRinging = true;
    doRing();

    s.intervalId = setInterval(() => {
      if (!stateRef.current.isRinging) {
        if (stateRef.current.intervalId) {
          clearInterval(stateRef.current.intervalId);
          stateRef.current.intervalId = null;
        }
        return;
      }
      doRing();
    }, 2500);
  }, [doRing]);

  const stopRinging = useCallback(() => {
    const s = stateRef.current;
    s.isRinging = false;

    if (s.intervalId !== null) {
      clearInterval(s.intervalId);
      s.intervalId = null;
    }

    try {
      if (navigator.vibrate) navigator.vibrate(0);
    } catch { /* ignore */ }

    setTimeout(() => {
      try {
        if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      } catch { /* ignore */ }
    }, 150);
  }, []);

  const triggerOnce = useCallback(() => {
    tryPlayRingTone().catch(() => { /* ignore */ });
    try {
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    return () => {
      const s = stateRef.current;
      s.isRinging = false;
      if (s.intervalId !== null) {
        clearInterval(s.intervalId);
        s.intervalId = null;
      }
      try {
        if (navigator.vibrate) navigator.vibrate(0);
      } catch { /* ignore */ }
    };
  }, []);

  return { startRinging, stopRinging, triggerOnce };
}
